import { GRADES } from './levels.js';
import type { Grade, LifetimeStats, RoundStats } from './types.js';

/**
 * Lifetime stat accumulation, as pure functions.
 *
 * The browser calls these against localStorage during solo practice and the
 * server calls the very same ones against `student_stats` at the end of a class
 * round. A badge therefore means the same thing in both places, which matters
 * the moment a student earns one at home and expects it on the class board.
 */

export const emptyLifetime = (): LifetimeStats => ({
  totalMatches: 0,
  totalRounds: 0,
  totalScore: 0,
  boardsCleared: 0,
  podiums: 0,
  dayStreak: 0,
  bestCombo: 0,
  bestScore: 0,
  clearedLevels: [],
  lastPlayedDay: null,
  gradesAced: [],
});

export const levelKey = (grade: Grade, level: number): string => `${grade}:${level}`;

/** Local calendar day — a streak is about the student's day, not UTC's. */
export function dayKey(d: Date = new Date()): string {
  const p = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`;
}

/** Days between two `YYYY-MM-DD` keys, ignoring clocks and DST. */
export function daysBetween(from: string, to: string): number {
  const ms = Date.parse(`${to}T00:00:00Z`) - Date.parse(`${from}T00:00:00Z`);
  return Math.round(ms / 86_400_000);
}

/**
 * A streak survives the same day (unchanged), extends on the next day, and
 * resets to 1 after any longer gap.
 */
export function nextStreak(lastDay: string | null, today: string, current: number): number {
  if (!lastDay) return 1;
  const gap = daysBetween(lastDay, today);
  if (gap <= 0) return Math.max(current, 1);
  if (gap === 1) return current + 1;
  return 1;
}

/** Every grade whose sub-levels have all been cleared at least once. */
export function gradesAcedFrom(clearedLevels: readonly string[]): Grade[] {
  const held = new Set(clearedLevels);
  return GRADES
    .filter((g) => g.levels.every((l) => held.has(levelKey(g.grade, l.index))))
    .map((g) => g.grade);
}

export interface RoundOutcome {
  round: RoundStats;
  grade: Grade;
  level: number;
  /** Finishing rank in a class session, 1-based. Null for solo practice. */
  rank?: number | null;
  playerCount?: number;
}

/** Folds one finished round into the lifetime totals. Never mutates its input. */
export function applyRound(
  lifetime: LifetimeStats,
  outcome: RoundOutcome,
  today: string = dayKey(),
): LifetimeStats {
  const { round, grade, level } = outcome;
  const clearedLevels = round.cleared && !lifetime.clearedLevels.includes(levelKey(grade, level))
    ? [...lifetime.clearedLevels, levelKey(grade, level)]
    : lifetime.clearedLevels;

  const podium = outcome.rank != null && outcome.rank <= 3 && (outcome.playerCount ?? 0) >= 3;

  return {
    totalMatches: lifetime.totalMatches + round.matches,
    totalRounds: lifetime.totalRounds + 1,
    totalScore: lifetime.totalScore + round.score,
    boardsCleared: lifetime.boardsCleared + (round.cleared ? 1 : 0),
    podiums: lifetime.podiums + (podium ? 1 : 0),
    dayStreak: nextStreak(lifetime.lastPlayedDay, today, lifetime.dayStreak),
    bestCombo: Math.max(lifetime.bestCombo, round.maxCombo),
    bestScore: Math.max(lifetime.bestScore, round.score),
    clearedLevels,
    lastPlayedDay: today,
    gradesAced: gradesAcedFrom(clearedLevels),
  };
}
